import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieSection from '@/pages/home/components/MovieSection';
import { articles } from '@/mocks/articles';
import { movies } from '@/mocks/movies';
import type { Article } from '@/mocks/articles';

const categoryNameMap: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
  documentary: '纪录片',
  kids: '少儿',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [articleId]);

  const article: Article | undefined = useMemo(() => {
    const found = articles.find((a) => a.id === articleId);
    if (found) return found;

    const movie = movies.find((m) => m.id === articleId);
    if (movie) {
      return {
        id: movie.id,
        title: `《${movie.title}》 - 在线观看`,
        summary: movie.description,
        content: `<h2>影片信息</h2><p>${movie.description}</p><h2>基本信息</h2><ul><li>导演：${movie.director}</li><li>主演：${movie.actors.join('、')}</li><li>年份：${movie.year}</li><li>地区：${movie.region}</li><li>类型：${movie.tags.join('、')}</li></ul><h2>播放信息</h2><p>本片支持${movie.updateInfo}高清播放，无需注册即可在线观看。悟空影视提供流畅不缓冲的观影体验，让您享受最佳的观影效果。</p><p>更多精彩内容，请浏览悟空影视其他分类。</p>`,
        category: movie.category,
        publishDate: `${movie.year}-01-01`,
        author: '悟空影视编辑',
        imageUrl: movie.imageUrl,
        tags: movie.tags,
        relatedMovieIds: movies.filter((m) => m.category === movie.category && m.id !== movie.id).slice(0, 5).map((m) => m.id),
      };
    }
    return undefined;
  }, [articleId]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的内容可能已被移除或不存在</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies.filter((m) => article.relatedMovieIds.includes(m.id));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '悟空影视',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.wukongyingshi.com/article/${article.id}`,
    },
  };

  const categoryName = categoryNameMap[article.category] || '影视';

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <article className="w-full px-4 md:px-6 pb-10">
            <div className="max-w-4xl mx-auto">
              <div className="relative aspect-[2/1] rounded-xl overflow-hidden bg-background-200 mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mb-6 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-price-tag-3-line"></i>
                  </div>
                  {article.tags.join(' · ')}
                </span>
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
                <span className="text-sm font-medium text-foreground-700 mr-2 whitespace-nowrap">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedMovies.length > 0 && (
            <MovieSection
              title="相关推荐"
              subtitle="您可能也喜欢这些内容"
              movies={relatedMovies}
            />
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}